import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import UbigeoSelector from './UbigeoSelector';
import { Direccion } from '../../types/comercio';

interface DireccionFormProps {
  value: Omit<Direccion, 'id'>;
  onChange: (direccion: Omit<Direccion, 'id'>) => void;
  error?: string;
  disabled?: boolean;
  title?: string;
  subtitle?: string;
}

const DireccionForm: React.FC<DireccionFormProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  title = "Dirección",
  subtitle
}) => {
  const handleUbigeoChange = (ubigeo: {
    departamento_id: string;
    provincia_id: string;
    distrito_id: string;
  }) => {
    onChange({
      ...value,
      ...ubigeo
    });
  };

  const handleFieldChange = (field: keyof Omit<Direccion, 'id'>, newValue: string) => {
    onChange({
      ...value,
      [field]: newValue
    });
  };

  return (
    <Paper elevation={1} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Calle y Número */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            fullWidth
            label="Calle / Avenida / Jirón"
            value={value.calle}
            onChange={(e) => handleFieldChange('calle', e.target.value)}
            error={!!error}
            disabled={disabled}
            required
            placeholder="Ej: Av. Javier Prado Este"
            sx={{ flex: 2 }}
          />
          
          <TextField
            label="Número"
            value={value.numero || ''}
            onChange={(e) => handleFieldChange('numero', e.target.value)}
            error={!!error}
            disabled={disabled}
            placeholder="Ej: 4200"
            sx={{ flex: 1, minWidth: 120 }}
          />
        </Box>

        {/* Selector de Ubigeos */}
        <UbigeoSelector
          value={{
            departamento_id: value.departamento_id,
            provincia_id: value.provincia_id,
            distrito_id: value.distrito_id
          }}
          onChange={handleUbigeoChange}
          error={error}
          disabled={disabled}
          required
        />

        {/* Referencia */}
        <TextField
          fullWidth
          label="Referencia (opcional)"
          value={value.referencia || ''}
          onChange={(e) => handleFieldChange('referencia', e.target.value)}
          error={!!error}
          disabled={disabled}
          placeholder="Ej: Frente al Centro Comercial, Al lado del banco, etc."
          multiline
          rows={2}
        />

        {/* Mostrar ubicación completa si está disponible */}
        {value.departamento_nombre && value.provincia_nombre && value.distrito_nombre && (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1, 
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Ubicación completa:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {value.calle}
              {value.numero && ` ${value.numero}`}
              <br />
              {value.distrito_nombre}, {value.provincia_nombre}, {value.departamento_nombre}
              {value.referencia && (
                <>
                  <br />
                  <em>Ref: {value.referencia}</em>
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default DireccionForm;
